/**
 * Who pays for a client's work, in words. Decision 123.
 *
 * A client under a parent is billed one of three ways: itself, its parent
 * with no c/- line (a council's depot, a chain's store), or itself c/- its
 * parent (an owner whose property an agency manages). The rule is the job's
 * own if it has one, else the client's, else the parent's for the clients
 * under it. The API resolves it (`services/bill-to.ts`); this file holds the
 * words so the client page, the job card and the Send window say the same.
 */
import { z } from 'zod'

export const BILLING_RULES = ['SELF', 'PARENT', 'CARE_OF_PARENT'] as const
export type BillingRuleKey = (typeof BILLING_RULES)[number]

export const CARE_OF_SEND_TO = ['PARENT', 'CLIENT', 'BOTH'] as const
export type CareOfSendToKey = (typeof CARE_OF_SEND_TO)[number]

/** Where the rule came from: the job, the client, its parent, or no parent. */
export type BillingRuleSource = 'JOB' | 'CLIENT' | 'PARENT' | 'NONE'

/**
 * The bill-to as one line: "Tom Smith c/- ABC Real Estate", "Shire of
 * Rockingham", "Tom Smith".
 */
export function billToLine(rule: BillingRuleKey, client: string, parent: string | null): string {
  if (!parent || rule === 'SELF') return client
  return rule === 'PARENT' ? parent : `${client} c/- ${parent}`
}

/** A rule as a choice on screen, naming the clients it is about. */
export function billingRuleChoice(rule: BillingRuleKey, client: string, parent: string): string {
  if (rule === 'PARENT') return `Bill ${parent}`
  if (rule === 'CARE_OF_PARENT') return `Bill ${client}, c/- ${parent}`
  return `Bill ${client} directly`
}

/** A parent's rule for the clients under it, as a choice on its page. */
export function childRuleChoice(rule: BillingRuleKey, parent: string): { label: string; detail: string } {
  if (rule === 'PARENT') {
    return { label: `Bill ${parent}`, detail: 'One bill here, whichever client under it the work was for.' }
  }
  if (rule === 'CARE_OF_PARENT') {
    return {
      label: `Bill the owner, c/- ${parent}`,
      detail: 'For properties it manages for someone else. The bill names the owner and comes here.',
    }
  }
  return { label: 'Bill each client directly', detail: 'Each client under it pays for its own work.' }
}

export const setClientBillingSchema = z.object({
  id: z.string().min(1),
  /** Null goes back to what the parent says. */
  billingRule: z.enum(BILLING_RULES).nullable().optional(),
  childBillingRule: z.enum(BILLING_RULES).optional(),
  careOfSendTo: z.enum(CARE_OF_SEND_TO).optional(),
})

export const setJobBillingRuleSchema = z.object({
  jobId: z.string().min(1),
  /** Null goes back to the client's rule. */
  billingRule: z.enum(BILLING_RULES).nullable(),
})
